import { ContentOptimizationTypeStatus, IRuntimeTermAssignment } from '@edgebox/sync-core-data-definitions';
import { ClientContentItemEntity, ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { BP_POPOVER_CLASSES, BP_TOOLBAR_BUTTON_CLASSES, BP_TOOLBAR_BUTTON_OPEN_CLASSES } from '../../../Components/brandPresenceClasses';
import { IFilterDropdownOption } from './FilterDropdownList';
import { MultiLevelDropdownPanel } from './MultiLevelDropdownPanel';
import { useDismissOnOutsideClick } from '../../../Components/useDismissOnOutsideClick';

/** What the selected pages are about to have done to them. */
export type ContentItemBulkAction = { kind: 'add-terms' } | { kind: 'remove-terms' } | { kind: 'optimize'; typeKey: string; label: string };

const ADD_TAGS_OPTION: IFilterDropdownOption = { id: 'add-terms', label: 'Add tags' };
const REMOVE_TAGS_OPTION: IFilterDropdownOption = { id: 'remove-terms', label: 'Remove tags' };
const OPTIMIZE_OPTION: IFilterDropdownOption = { id: 'optimize', label: 'Optimize', interactive: true };
const ACTION_OPTIONS = [ADD_TAGS_OPTION, REMOVE_TAGS_OPTION, OPTIMIZE_OPTION];

const CHOOSE_ACTION_LABEL = 'Choose action';

function toIssueTypeKeys(item: ClientContentItemEntity, issueTypeTaxonomyId: string): Set<string> {
  const assignments: IRuntimeTermAssignment[] = item.terms?.assigned || [];
  const keys = assignments
    .filter((assignment) => String(assignment.taxonomy.getId()) === issueTypeTaxonomyId)
    .map((assignment) => assignment.term.getStableKey())
    .map((key) => key.trim())
    .filter((key) => key.length > 0);

  return new Set(keys);
}

function getCommonIssueTypeKeys(selectedItems: ClientContentItemEntity[], issueTypeTaxonomyId?: string): Set<string> {
  if (!issueTypeTaxonomyId || selectedItems.length === 0) {
    return new Set();
  }

  const [first, ...rest] = selectedItems;
  const intersection = toIssueTypeKeys(first, issueTypeTaxonomyId);

  for (const item of rest) {
    const keys = toIssueTypeKeys(item, issueTypeTaxonomyId);
    for (const existing of Array.from(intersection)) {
      if (!keys.has(existing)) {
        intersection.delete(existing);
      }
    }
  }

  return intersection;
}

function actionLabel(action: ContentItemBulkAction | undefined): string {
  if (!action) {
    return CHOOSE_ACTION_LABEL;
  }

  if (action.kind === 'optimize') {
    return `Optimize: ${action.label}`;
  }

  return action.kind === 'add-terms' ? ADD_TAGS_OPTION.label : REMOVE_TAGS_OPTION.label;
}

/**
 * The one menu of things to do with the selected pages: add tags, remove tags, or optimize them
 * with one of the optimization types every selected page qualifies for. What the chosen action
 * still needs — the tags, or nothing — is asked for next to it by the caller.
 */
export function ContentItemActionDropdown({
  api,
  selectedItems,
  issueTypeTaxonomyId,
  action,
  onActionChange,
}: {
  api?: ISyncCoreApi;
  selectedItems: ClientContentItemEntity[];
  issueTypeTaxonomyId?: string;
  action?: ContentItemBulkAction;
  onActionChange: (action: ContentItemBulkAction | undefined) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const [optimizeOpen, setOptimizeOpen] = React.useState(false);
  const [typeSearch, setTypeSearch] = React.useState('');
  const [pendingTypeKey, setPendingTypeKey] = React.useState<string | undefined>(undefined);
  const [loadingTypes, setLoadingTypes] = React.useState(false);
  const [availableTypeOptions, setAvailableTypeOptions] = React.useState<IFilterDropdownOption[]>([]);

  const close = React.useCallback(() => {
    setOpen(false);
    setOptimizeOpen(false);
    setTypeSearch('');
  }, []);

  const commonIssueTypeKeys = React.useMemo(
    () => getCommonIssueTypeKeys(selectedItems, issueTypeTaxonomyId),
    [selectedItems, issueTypeTaxonomyId]
  );

  React.useEffect(() => {
    if (!open || !optimizeOpen || !api) {
      return;
    }

    let active = true;
    void (async () => {
      setLoadingTypes(true);
      const listed = await api.governance.contentOptimizationTypes.list(
        { page: 0, itemsPerPage: 200 },
        { statuses: [ContentOptimizationTypeStatus.Active] }
      );

      if (!active) {
        return;
      }

      const byKey = new Map<string, IFilterDropdownOption>();
      for (const type of listed.items || []) {
        const typeKey = String(type.key || '').trim();
        if (!typeKey) {
          continue;
        }

        const matchesCommonIssue = (type.targetIssueTypes || []).some((targetIssueType) =>
          commonIssueTypeKeys.has(targetIssueType.getStableKey())
        );

        if (!matchesCommonIssue) {
          continue;
        }

        byKey.set(typeKey, {
          id: typeKey,
          label: String(type.name || typeKey),
        });
      }

      setAvailableTypeOptions(Array.from(byKey.values()).sort((left, right) => left.label.localeCompare(right.label)));
      setLoadingTypes(false);
    })();

    return () => {
      active = false;
    };
  }, [api, commonIssueTypeKeys, open, optimizeOpen]);

  // A selection change can take away the type that was chosen; the action goes with it.
  React.useEffect(() => {
    if (action?.kind !== 'optimize' || loadingTypes || !optimizeOpen) {
      return;
    }

    if (!availableTypeOptions.some((option) => option.id === action.typeKey)) {
      onActionChange(undefined);
    }
  }, [action, availableTypeOptions, loadingTypes, onActionChange, optimizeOpen]);

  const filteredTypeOptions = React.useMemo(() => {
    const needle = typeSearch.trim().toLowerCase();
    if (!needle) {
      return availableTypeOptions;
    }

    return availableTypeOptions.filter((option) => option.label.toLowerCase().includes(needle));
  }, [availableTypeOptions, typeSearch]);

  const emptyTypesMessage = React.useMemo(() => {
    if (commonIssueTypeKeys.size === 0) {
      return 'No shared issue type was found across the selected content items.';
    }

    if (!loadingTypes && availableTypeOptions.length === 0) {
      return 'No optimization type matches the issue types shared by all selected items.';
    }

    return undefined;
  }, [availableTypeOptions.length, commonIssueTypeKeys.size, loadingTypes]);

  const selectedActionOptions = React.useMemo(() => {
    const selected = ACTION_OPTIONS.find((option) => option.id === action?.kind);
    return selected ? [selected] : [];
  }, [action]);

  useDismissOnOutsideClick({ open, containerRef, triggerRef, onDismiss: close });

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`${open ? BP_TOOLBAR_BUTTON_OPEN_CLASSES : BP_TOOLBAR_BUTTON_CLASSES} min-w-[200px] max-w-[320px] justify-between ${
          !open && action ? 'text-slate-900' : ''
        }`}
        onClick={() => {
          if (open) {
            close();
            return;
          }

          setPendingTypeKey(action?.kind === 'optimize' ? action.typeKey : undefined);
          setOpen(true);
        }}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <span className="truncate">{actionLabel(action)}</span>
        <FontAwesomeIcon icon={faChevronDown} className={`text-xs transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open ? (
        <div className={`absolute right-0 top-full z-20 mt-2 w-[360px] ${BP_POPOVER_CLASSES}`}>
          <MultiLevelDropdownPanel
            selectedTarget={optimizeOpen ? { id: OPTIMIZE_OPTION.id, label: OPTIMIZE_OPTION.label } : undefined}
            targetSearch=""
            onTargetSearchChange={() => undefined}
            targetSearchPlaceholder="Search actions"
            selectedTargetOptions={selectedActionOptions}
            filteredTargetOptions={ACTION_OPTIONS}
            onSelectTarget={(option) => {
              if (option.id === OPTIMIZE_OPTION.id) {
                setOptimizeOpen(true);
                setTypeSearch('');
                return;
              }

              onActionChange({ kind: option.id === ADD_TAGS_OPTION.id ? 'add-terms' : 'remove-terms' });
              close();
            }}
            valueSearch={typeSearch}
            onValueSearchChange={setTypeSearch}
            valueSearchPlaceholder="Search optimization types"
            selectedValueOptions={pendingTypeKey ? availableTypeOptions.filter((option) => option.id === pendingTypeKey) : []}
            filteredValueOptions={filteredTypeOptions}
            loadingValues={loadingTypes}
            valueSelectionMultiple={false}
            onToggleValue={(option) => setPendingTypeKey(option.id === pendingTypeKey ? undefined : option.id)}
            hideSelectedValuesFromFiltered={false}
            changeTargetLabel="Change action"
            onBackToTargets={() => {
              setOptimizeOpen(false);
              setTypeSearch('');
            }}
            onCancel={close}
            onApply={() => {
              const chosen = availableTypeOptions.find((option) => option.id === pendingTypeKey);
              if (chosen) {
                onActionChange({ kind: 'optimize', typeKey: chosen.id, label: chosen.label });
              }
              close();
            }}
            disableApply={!pendingTypeKey}
            applyLabel="Select"
            emptyValuesMessage={emptyTypesMessage}
          />
        </div>
      ) : null}
    </div>
  );
}
